import React from "react";
import { motion } from "framer-motion";
import { Cpu, Laptop, Factory, ClipboardList, Sun, Cctv, Zap, Wrench, Globe, ArrowUpRight } from "lucide-react";
import { SERVICES } from "@/config";
import { useI18n } from "@/i18n";

const ICONS = {
  cpu: Cpu,
  laptop: Laptop,
  factory: Factory,
  "clipboard-list": ClipboardList,
  sun: Sun,
  cctv: Cctv,
  zap: Zap,
  wrench: Wrench,
  globe: Globe,
};

export const Services = () => {
  const { t } = useI18n();
  const go = (id) => document.querySelector(id)?.scrollIntoView({ behavior: "smooth" });

  return (
    <section id="services" className="relative py-24 bg-[#f8fafc]" data-testid="services-section">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="max-w-2xl mb-14">
          <p className="text-xs uppercase tracking-[0.2em] font-bold text-brand mb-3">{t.services.eyebrow}</p>
          <h2 className="font-head text-3xl sm:text-4xl font-semibold text-navy tracking-tight">
            {t.services.title}
          </h2>
          <p className="mt-4 text-slate-600">
            {t.services.subtitle}
          </p>
        </div>

        {/* Control-room dense grid with shared 1px borders */}
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 border-t border-l border-navy/10 rounded-md overflow-hidden bg-white">
          {SERVICES.map((s, i) => {
            const Icon = ICONS[s.icon] || Cpu;
            return (
              <motion.button
                key={s.title}
                data-testid={`service-card-${i}`}
                onClick={() => go("#booking")}
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true, margin: "-40px" }}
                transition={{ duration: 0.4, delay: (i % 3) * 0.08 }}
                className="group text-left p-8 border-r border-b border-navy/10 hover:bg-navy/[0.02] transition-colors relative"
              >
                <div className="flex items-start justify-between">
                  <div className="w-12 h-12 rounded-md bg-navy/5 group-hover:bg-brand/10 flex items-center justify-center transition-colors">
                    <Icon className="w-6 h-6 text-navy group-hover:text-brand transition-colors" />
                  </div>
                  <ArrowUpRight className="w-5 h-5 text-slate-300 group-hover:text-brand group-hover:translate-x-0.5 group-hover:-translate-y-0.5 transition-all" />
                </div>
                <h3 className="font-head text-lg font-semibold text-navy mt-5">{t.services.items[i]?.title || s.title}</h3>
                <p className="mt-2 text-sm text-slate-600 leading-relaxed">{t.services.items[i]?.desc || s.desc}</p>
                <span className="absolute bottom-0 left-0 h-0.5 w-0 bg-brand group-hover:w-full transition-all duration-300" />
              </motion.button>
            );
          })}
          {/* filler cell to complete grid on lg */}
          <div className="hidden lg:block lg:col-start-2 border-r border-b border-navy/10 p-8 bg-navy dot-matrix">
            <div className="h-full flex flex-col justify-end">
              <p className="font-head text-white text-lg font-semibold">{t.services.notFound}</p>
              <button
                data-testid="services-cta-request"
                onClick={() => go("#booking")}
                className="btn-lift mt-4 self-start bg-brand hover:bg-brand-hover text-white text-sm font-semibold px-5 py-2.5 rounded-md"
              >
                {t.services.cta}
              </button>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
};
